// src/pages/operations-dashboard/components/DeliveryPerformanceSection.jsx
import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  AreaChart,
  Area,
} from "recharts";
import { Target, TrendingUp, CheckCircle, RotateCcw, AlertTriangle } from "lucide-react";
import { formatCompact, formatPercent, formatShortDate } from "../../../utils/dashboardConstants";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white/95 backdrop-blur-lg border border-gray-100 rounded-xl shadow-2xl px-4 py-3">
      <p className="text-xs font-semibold text-gray-500 mb-2">{formatShortDate(label)}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center justify-between gap-4 py-0.5">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-xs text-gray-600">{entry.name}</span>
          </div>
          <span className="text-xs font-semibold text-gray-800">{formatPercent(entry.value)}</span>
        </div>
      ))}
    </div>
  );
};

const GaugeRing = ({ percentage, label, color, bgColor, size = 100 }) => {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(percentage, 100) / 100) * circumference;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={bgColor} strokeWidth={8} />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={8}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-bold text-gray-900">{formatPercent(percentage)}</span>
        </div>
      </div>
      <span className="text-xs font-medium text-gray-500">{label}</span>
    </div>
  );
};

const Skeleton = ({ className = "" }) => (
  <div className={`bg-gray-100 rounded-lg animate-pulse ${className}`} />
);

export default function DeliveryPerformanceSection({ data, loading }) {
  const isLoading = loading?.deliveryPerformance;

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <Skeleton className="h-6 w-48 mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-64 mt-6" />
      </div>
    );
  }

  if (!data) return null;

  const deliveryRate = data.delivery_percentage || 0;
  const rtoRate = data.rto_percentage || 0;
  const ndrRate = data.ndr_percentage || 0;
  const cancelRate = data.cancellation_percentage || 0;

  const dailyData = (data.daily_delivery_performance || []).map((d) => ({
    date: d.date,
    "Delivery %": d.delivery_percentage || 0,
    "RTO %": d.rto_percentage || 0,
    "NDR %": d.ndr_percentage || 0,
  }));

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-50">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-green-50">
            <Target className="w-4.5 h-4.5 text-green-500" strokeWidth={2} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Delivery Performance</h3>
            <p className="text-xs text-gray-400 mt-0.5">Success rates and performance metrics</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Gauge Rings */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          <div className="flex flex-col items-center p-4 bg-emerald-50/50 rounded-2xl border border-emerald-100">
            <GaugeRing percentage={deliveryRate} label="Delivery Rate" color="#10b981" bgColor="#d1fae5" size={96} />
            <div className="flex items-center gap-1 mt-2">
              <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
              <span className="text-xs font-semibold text-emerald-600">{formatCompact(data.delivered_count || 0)} delivered</span>
            </div>
          </div>

          <div className="flex flex-col items-center p-4 bg-red-50/50 rounded-2xl border border-red-100">
            <GaugeRing percentage={rtoRate} label="RTO Rate" color="#ef4444" bgColor="#fee2e2" size={96} />
            <div className="flex items-center gap-1 mt-2">
              <RotateCcw className="w-3.5 h-3.5 text-red-500" />
              <span className="text-xs font-semibold text-red-600">{formatCompact(data.rto_count || 0)} returned</span>
            </div>
          </div>

          <div className="flex flex-col items-center p-4 bg-amber-50/50 rounded-2xl border border-amber-100">
            <GaugeRing percentage={ndrRate} label="NDR Rate" color="#f59e0b" bgColor="#fef3c7" size={96} />
            <div className="flex items-center gap-1 mt-2">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-xs font-semibold text-amber-600">{formatCompact(data.ndr_count || 0)} NDR</span>
            </div>
          </div>

          <div className="flex flex-col items-center p-4 bg-gray-50/50 rounded-2xl border border-gray-200">
            <GaugeRing percentage={cancelRate} label="Cancel Rate" color="#6b7280" bgColor="#f3f4f6" size={96} />
            <div className="flex items-center gap-1 mt-2">
              <TrendingUp className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-xs font-semibold text-gray-600">{formatCompact(data.total_shipments || 0)} total</span>
            </div>
          </div>
        </div>

        {/* Trend Chart */}
        {dailyData.length > 0 ? (
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Daily Performance Trend</h4>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={dailyData} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                <defs>
                  <linearGradient id="gradDelivery" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradRTO" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradNDR" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatShortDate}
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                  axisLine={{ stroke: "#e2e8f0" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `${v}%`}
                  domain={[0, 100]}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 12 }} />
                <Area type="monotone" dataKey="Delivery %" stroke="#10b981" strokeWidth={2.5} fill="url(#gradDelivery)" dot={false} activeDot={{ r: 4 }} />
                <Area type="monotone" dataKey="RTO %" stroke="#ef4444" strokeWidth={2} fill="url(#gradRTO)" dot={false} activeDot={{ r: 4 }} />
                <Area type="monotone" dataKey="NDR %" stroke="#f59e0b" strokeWidth={2} fill="url(#gradNDR)" dot={false} activeDot={{ r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-48 flex items-center justify-center text-gray-400 text-sm">
            No daily performance data available
          </div>
        )}
      </div>
    </div>
  );
}
